<template>
	<Container :padding="0" :direction="direction" :gap="12" overflow="visible">
		<Container
			v-for="(option, idx) in options"
			:key="idx"
			:padding="0"
			direction="row"
			:gap="8"
			overflow="visible"
		>
			<component
				:is="componentToRender"
				:state="state"
				:disabled="disabled"
				:checked="isChecked(option)"
				:data-qa-field="`${dataQaId}-${getOptionDataQa(option)}`"
				@input="handleInput($event, option)"
			/>
			<Typography
				type="p2"
				color="dark"
				:data-qa="`${dataQaId}-${getOptionDataQa(option)}`"
				@click.native="handleInput($event, option)"
			>
				{{ getOptionLabel(option) }}
			</Typography>
			<slot :option="option" name="option_info"></slot>
		</Container>
	</Container>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import Container, { ContainerDirection } from "./../Container/Container.vue";
import Checkbox from "./../FormElements/Checkbox.vue";
import RadioButton from "./../FormElements/RadioButton.vue";
import Typography from "./../Typography/Typography.vue";

export type OptionDetails = {
	header: string;
	id: string;
};

export default defineComponent({
	name: "CheckOptions",
	components: {
		Container,
		Checkbox,
		RadioButton,
		Typography
	},
	props: {
		value: {
			type: [String, Array, Object] as PropType<
				string | string[] | OptionDetails | OptionDetails[]
			>,
			default: null
		},
		options: {
			type: Array as PropType<string[] | OptionDetails[]>,
			default: () => []
		},
		direction: {
			type: String as PropType<ContainerDirection>,
			default: "row"
		},
		type: {
			type: String,
			default: "checkbox"
		},
		state: {
			type: String,
			default: "default"
		},
		dataQaId: {
			type: String,
			default: ""
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		componentToRender() {
			if (this.type === "checkbox") {
				return "Checkbox";
			} else {
				return "RadioButton";
			}
		},
		getTypeOfValue() {
			if (!this.value) {
				return null;
			}

			const isArray = Array.isArray(this.value);
			const typeOfValue = typeof this.value;

			if (typeOfValue === "object" && !isArray) {
				return "object";
			} else if (typeOfValue === "object" && isArray) {
				return "array";
			} else if (typeOfValue === "string") {
				return "string";
			} else {
				return null;
			}
		},
		getTypeOfOptions() {
			const options = this.options;
			const hasOptions = options.length;
			const [firstOption] = options;
			if (hasOptions && typeof firstOption === "string") {
				return "string";
			} else if (hasOptions && typeof firstOption === "object") {
				return "object";
			} else {
				return null;
			}
		}
	},
	methods: {
		getOptionLabel(option: string | OptionDetails) {
			const typeOfOption = this.getTypeOfOptions;

			if (typeOfOption === "object") {
				return (option as OptionDetails).header;
			} else {
				return option;
			}
		},
		getOptionDataQa(option: string | OptionDetails) {
			if (!option) {
				return;
			}
			const typeOfOption = this.getTypeOfOptions;

			if (typeOfOption === "object") {
				return (option as OptionDetails).id.replace(/\s+/g, "-").toLowerCase();
			} else {
				return (option as string).replace(/\s+/g, "-").toLowerCase();
			}
		},
		isChecked(option: string | OptionDetails) {
			if (!this.value) {
				return;
			}

			const typeOfValue = this.getTypeOfValue;
			const typeOfOption = this.getTypeOfOptions;

			if (this.type === "radio") {
				if (typeOfValue === "object" && typeOfOption === "object") {
					return (option as OptionDetails).header === (this.value as OptionDetails).header;
				} else {
					return option === this.value;
				}
			}

			if (this.type === "checkbox" && typeOfValue === "array") {
				return this.indexOfCheckedCheckbox(option) > -1;
			}
			return false;
		},
		indexOfCheckedCheckbox(option: string | OptionDetails) {
			const typeOfOption = this.getTypeOfOptions;

			return (this.value as string[] | OptionDetails[]).findIndex(selected => {
				const checkOption = typeOfOption === "object" ? (option as OptionDetails).id : option;
				const selectedValue = typeOfOption === "object" ? (selected as OptionDetails).id : selected;
				return selectedValue === checkOption;
			});
		},
		handleInput(e: Event, option: string | OptionDetails) {
			if (this.type === "radio") {
				this.$emit("input", option);
				return;
			}

			if (this.type === "checkbox") {
				const selectedValues = this.value as string[] | OptionDetails[];
				if (!selectedValues) {
					this.$emit("input", [option]);
					return;
				}

				const indexOfCheckbox = this.indexOfCheckedCheckbox(option);

				if (indexOfCheckbox > -1) {
					selectedValues.splice(indexOfCheckbox, 1);
				} else {
					selectedValues.push(option as string & OptionDetails);
				}
				this.$emit("input", selectedValues);
			}
		}
	}
});
</script>
